/**
 * Validación de las respuestas en el borde (Zod). Los tipos de la app son los del contrato
 * (esquema.d.ts, generado); estos esquemas comprueban en ejecución que la respuesta los cumpla y
 * descartan cualquier campo que el contrato no declare.
 * Las pruebas de tipos del final fallan en compilación si el contrato y estos esquemas se separan.
 */
import { z } from "zod";
import type { components } from "./esquema";

type S = components["schemas"];

const texto = z.string().nullable();
const perfil = z.enum(["operaciones", "finanzas"]);
const estadoUsuario = z.enum(["invitado", "activo", "suspendido", "baja"]);
const estadoAcceso = z.enum(["activo", "suspendido", "baja"]);
const relacion = z.enum(["propia", "mismo_grupo", "agente_aduanas", "casa_matriz", "otra"]);

const usuario = z.object({
  usuarioId: z.string(),
  correo: z.string(),
  nombre: z.string(),
  estado: estadoUsuario,
  invitadoPor: z.string(),
  invitadoEn: z.string(),
  ultimoIngreso: texto,
});

const acceso = z.object({
  institucion: z.string(),
  perfil,
  estado: estadoAcceso,
  relacion,
  motivo: texto,
  concedidoPor: z.string(),
  concedidoEn: z.string(),
});

const usuarioConAccesos = z.object({ usuario, accesos: z.array(acceso) });

const institucion = z.object({
  institucion: z.string(),
  nombre: z.string(),
  habilitada: z.boolean(),
  actualizadoPor: z.string(),
  actualizadoEn: z.string(),
});

const registro = z.object({
  momento: z.string(),
  usuarioId: z.string(),
  tipo: z.string(),
  actor: texto,
  accion: texto,
  recurso: texto,
  detalle: z.record(z.string(), texto),
});

export const esquemaUsuarios = z.object({ usuarios: z.array(usuarioConAccesos) });
export const esquemaUsuario = usuario;
export const esquemaFicha = z.object({ usuario, accesos: z.array(acceso) });
export const esquemaInstituciones = z.object({ instituciones: z.array(institucion) });
export const esquemaPagina = z.object({ registros: z.array(registro), siguiente: texto });
export const esquemaHecho = z.object({ ok: z.literal(true) });
export const esquemaError = z.object({ codigo: z.string(), mensaje: z.string() });

// ---- Pruebas de tipos: los campos de cada esquema son exactamente los del contrato. ----
type MismasLlaves<A, B> = [keyof A] extends [keyof B] ? ([keyof B] extends [keyof A] ? true : false) : false;
type Cierto<T extends true> = T;
type MismoEnum<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
export type PruebasDeContrato = [
  Cierto<MismasLlaves<z.output<typeof usuario>, S["Usuario"]>>,
  Cierto<MismasLlaves<z.output<typeof acceso>, S["Acceso"]>>,
  Cierto<MismasLlaves<z.output<typeof usuarioConAccesos>, S["UsuarioConAccesos"]>>,
  Cierto<MismasLlaves<z.output<typeof esquemaFicha>, S["Ficha"]>>,
  Cierto<MismasLlaves<z.output<typeof institucion>, S["Institucion"]>>,
  Cierto<MismasLlaves<z.output<typeof registro>, S["RegistroAuditoria"]>>,
  Cierto<MismasLlaves<z.output<typeof esquemaPagina>, S["PaginaAuditoria"]>>,
  Cierto<MismoEnum<z.output<typeof perfil>, S["Perfil"]>>,
  Cierto<MismoEnum<z.output<typeof estadoUsuario>, S["EstadoUsuario"]>>,
  Cierto<MismoEnum<z.output<typeof estadoAcceso>, S["EstadoAcceso"]>>,
  Cierto<MismoEnum<z.output<typeof relacion>, S["Relacion"]>>,
];
